import { getDefinition } from "../catalog";
import {
  effectiveTerminals,
  isStudTerminal,
} from "../effective";
import { migrate } from "../schema/migrate";
import { parseFuseMarker } from "./fuse-marker";
import { placeAddedNodes } from "./system-edit-placement";
import {
  ambiguousEnginePort,
  ambiguousEnginePortRejection,
  buildDraftNode,
  isReturnBar,
  layoutGeneratedComponents,
  pickTerminal,
  seriesLinkHonoured,
  SOURCE_RANK,
  terminalRole,
  type TerminalRole,
} from "./system-draft";
import type {
  Cable,
  ComponentDefinition,
  ComponentNode,
  System,
  SystemNode,
} from "../types";
import type {
  AiSystemPolarity,
  AiSystemPort,
} from "../../../functions/_ai/system-schema";
import type { AiSystemEditDraft } from "../../../functions/_ai/system-edit-schema";

/**
 * Apply a model's **delta** to a system that already exists. Nothing the user did
 * not ask about changes: existing nodes and cables keep every field, and no
 * electrical number is decided here — wire size and fuse are the engine's.
 *
 * Deterministic — no `nanoid`, no `Date.now()` — so the endpoint's answer is
 * cacheable. Edge-safe: the Pages Function bundles it, so nothing here may reach
 * `import.meta.env`, `posthog-js` or `@supabase/*`, which is why the fusing pass
 * stays in `system-fuse.ts`.
 */

export type SystemEditError =
  | { kind: "unknown-catalog-id"; refs: string[]; catalogIds: string[] }
  | { kind: "ambiguous-engine-port"; ref: string }
  | { kind: "unknown-ref"; refs: string[] }
  | { kind: "duplicate-ref"; refs: string[] }
  | {
      kind: "no-terminal";
      ref: string;
      polarity: AiSystemPolarity;
      reason: "missing" | "exhausted";
      available: number;
    }
  | { kind: "invalid-system"; message: string };

export type SystemEditResult =
  | {
      ok: true;
      system: System;
      addedNodeIds: string[];
      /** `fuseGeneratedSystem` fuses these and only these — a cable already on
       *  the canvas unfused must not acquire a fuse. */
      addedCableIds: string[];
      removedNodeIds: string[];
      removedCableIds: string[];
      /** Cables each added connection became, indexed by `draft.connections`. */
      cableIdsByConnection: string[][];
    }
  | { ok: false; error: SystemEditError };

export function applySystemEdit(
  system: System,
  refToNodeId: ReadonlyMap<string, string>,
  draft: AiSystemEditDraft,
): SystemEditResult {
  const nodeById = new Map(system.nodes.map((n) => [n.id, n]));

  const newRefs = new Set(draft.components.map((c) => c.ref));
  const collisions = draft.components
    .map((c) => c.ref)
    .filter((ref) => refToNodeId.has(ref));
  if (collisions.length) {
    return { ok: false, error: { kind: "duplicate-ref", refs: collisions } };
  }

  const unknown = new Set<string>();
  const knows = (ref: string) => refToNodeId.has(ref) || newRefs.has(ref);
  for (const conn of draft.connections) {
    if (!knows(conn.from)) unknown.add(conn.from);
    if (!knows(conn.to)) unknown.add(conn.to);
  }
  for (const ref of draft.removeComponents) {
    if (!refToNodeId.has(ref)) unknown.add(ref);
  }
  for (const rem of draft.removeConnections) {
    if (!refToNodeId.has(rem.from)) unknown.add(rem.from);
    if (!refToNodeId.has(rem.to)) unknown.add(rem.to);
  }
  if (unknown.size) {
    return { ok: false, error: { kind: "unknown-ref", refs: [...unknown] } };
  }

  const defs = new Map<string, ComponentDefinition>();
  const unknownRefs: string[] = [];
  const unknownIds: string[] = [];
  for (const c of draft.components) {
    const def = getDefinition(c.catalogId);
    if (!def) {
      unknownRefs.push(c.ref);
      if (!unknownIds.includes(c.catalogId)) unknownIds.push(c.catalogId);
      continue;
    }
    defs.set(c.ref, def);
  }
  if (unknownIds.length) {
    return {
      ok: false,
      error: { kind: "unknown-catalog-id", refs: unknownRefs, catalogIds: unknownIds },
    };
  }

  // A removed part takes its cables with it: a dangling cable is not a system
  // the app can load.
  const removedNodeIds = new Set(
    draft.removeComponents.map((ref) => refToNodeId.get(ref)!),
  );
  const removedCableIds = new Set<string>();
  for (const cable of system.cables) {
    if (removedNodeIds.has(cable.fromNodeId) || removedNodeIds.has(cable.toNodeId)) {
      removedCableIds.add(cable.id);
    }
  }
  for (const rem of draft.removeConnections) {
    const a = refToNodeId.get(rem.from)!;
    const b = refToNodeId.get(rem.to)!;
    for (const cable of system.cables) {
      const between =
        (cable.fromNodeId === a && cable.toNodeId === b) ||
        (cable.fromNodeId === b && cable.toNodeId === a);
      if (between && cablePolarity(cable, nodeById) === rem.polarity) {
        removedCableIds.add(cable.id);
      }
    }
  }

  const survivingNodes = system.nodes.filter((n) => !removedNodeIds.has(n.id));
  const survivingCables = system.cables.filter((c) => !removedCableIds.has(c.id));

  // Ids are uniquified against the *original* system, removed ones included —
  // undo restores those.
  const usedIds = new Set(system.nodes.map((n) => n.id));
  const nodeIdsByRef = new Map<string, string[]>();
  const addedNodes: ComponentNode[] = [];
  for (const c of draft.components) {
    const def = defs.get(c.ref)!;
    const base = slug(c.ref) || "part";
    const ids: string[] = [];
    for (let i = 0; i < c.count; i++) {
      const id = uniqueId(c.count === 1 ? base : `${base}-${i + 1}`, usedIds);
      ids.push(id);
      addedNodes.push(buildDraftNode(id, def, c, i));
    }
    nodeIdsByRef.set(c.ref, ids);
  }
  for (const [ref, nodeId] of refToNodeId) {
    if (!removedNodeIds.has(nodeId)) nodeIdsByRef.set(ref, [nodeId]);
  }

  // Copied, never mutated: `system` is the store's live object and this planner
  // re-runs on every chip edit, so an in-place stud count would leak onto the
  // canvas before the user pressed anything.
  const nodes: SystemNode[] = [
    ...survivingNodes.map((n) => ({ ...n })),
    ...addedNodes,
  ];
  const defByNodeId = new Map<string, ComponentDefinition>();
  for (const node of nodes) {
    if (node.kind !== "component") continue;
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (def) defByNodeId.set(node.id, def);
  }

  // Same pairing rule as `draftToSystem`: two batteries onto two chargers is two
  // cables, not four, and a lone unit on one end is shared by every unit on the
  // other.
  type PendingCable = {
    fromNodeId: string;
    toNodeId: string;
    fromRef: string;
    toRef: string;
    fromPort: AiSystemPort;
    toPort: AiSystemPort;
    polarity: AiSystemPolarity;
    lengthM: number;
    lengthEstimated: boolean;
    marker: string | null;
    seriesLink: boolean;
    connectionIndex: number;
  };
  const pending: PendingCable[] = [];
  draft.connections.forEach((conn, connectionIndex) => {
    const fromIds = nodeIdsByRef.get(conn.from) ?? [];
    const toIds = nodeIdsByRef.get(conn.to) ?? [];
    // A cable onto a part this same delta removes: dropped, since a re-ask
    // learns nothing the delta doesn't say.
    if (!fromIds.length || !toIds.length) return;
    const fanOut = Math.max(fromIds.length, toIds.length);
    for (let i = 0; i < fanOut; i++) {
      const fromNodeId = fromIds[i % fromIds.length];
      const toNodeId = toIds[i % toIds.length];
      if (fromNodeId === toNodeId) continue;
      pending.push({
        connectionIndex,
        fromNodeId,
        toNodeId,
        fromRef: conn.from,
        toRef: conn.to,
        fromPort: conn.fromPort,
        toPort: conn.toPort,
        polarity: conn.polarity,
        lengthM: conn.lengthM,
        lengthEstimated: conn.lengthEstimated,
        marker: conn.marker,
        seriesLink: seriesLinkHonoured(
          conn.seriesLink,
          defByNodeId.get(fromNodeId),
          defByNodeId.get(toNodeId),
        ),
      });
    }
  });

  // Grow busbars, never shrink one: `studCount` synthesizes the terminal ids
  // existing cables stand on, so taking a stud away orphans one.
  const landings = new Map<string, number>();
  for (const c of survivingCables) {
    landings.set(c.fromNodeId, (landings.get(c.fromNodeId) ?? 0) + 1);
    landings.set(c.toNodeId, (landings.get(c.toNodeId) ?? 0) + 1);
  }
  for (const p of pending) {
    landings.set(p.fromNodeId, (landings.get(p.fromNodeId) ?? 0) + 1);
    landings.set(p.toNodeId, (landings.get(p.toNodeId) ?? 0) + 1);
  }
  for (const node of nodes) {
    if (node.kind !== "component") continue;
    const def = defByNodeId.get(node.id);
    if (def?.renderVariant !== "busbar") continue;
    const landing = landings.get(node.id) ?? 1;
    // Same rule as `draftToSystem`: one cable claims the input post, except on a
    // return bar whose post faces away from the loads.
    const wanted = Math.max(1, isReturnBar(def) ? landing : landing - 1);
    const current = node.studCount ?? countStuds(def, node);
    node.studCount = Math.max(current, wanted);
  }

  // The occupancy map starts full, so a new branch is never handed a slot an
  // existing circuit is already on.
  const stackedByNode = new Map<string, Map<string, number>>();
  const stackedAt = (nodeId: string) => {
    const found = stackedByNode.get(nodeId);
    if (found) return found;
    const fresh = new Map<string, number>();
    stackedByNode.set(nodeId, fresh);
    return fresh;
  };
  for (const c of survivingCables) {
    const from = stackedAt(c.fromNodeId);
    from.set(c.fromTerminalId, (from.get(c.fromTerminalId) ?? 0) + 1);
    const to = stackedAt(c.toNodeId);
    to.set(c.toTerminalId, (to.get(c.toTerminalId) ?? 0) + 1);
  }

  const incident = new Map<string, number[]>();
  pending.forEach((p, i) => {
    push(incident, p.fromNodeId, i);
    push(incident, p.toNodeId, i);
  });

  const nodeAfterEdit = new Map(
    nodes.filter((n): n is ComponentNode => n.kind === "component").map((n) => [n.id, n]),
  );
  const resolved = new Map<string, string>();

  for (const [nodeId, cableIndexes] of incident) {
    const node = nodeAfterEdit.get(nodeId);
    const def = defByNodeId.get(nodeId);
    if (!node || !def) {
      return {
        ok: false,
        error: { kind: "invalid-system", message: `unknown node ${nodeId}` },
      };
    }
    const terminals = effectiveTerminals(def, node);
    const stacked = stackedAt(nodeId);

    // Most-upstream cable first, so the bank's feed claims a bar's input post
    // rather than whichever load the model listed first.
    const ordered = [...cableIndexes].sort((a, b) => {
      const rankA = rankOfOtherEnd(pending[a], nodeId, defByNodeId);
      const rankB = rankOfOtherEnd(pending[b], nodeId, defByNodeId);
      return rankA - rankB || a - b;
    });

    for (const index of ordered) {
      const p = pending[index];
      const isFrom = p.fromNodeId === nodeId;
      const end = isFrom ? "from" : "to";
      const port = isFrom ? p.fromPort : p.toPort;
      const otherDef = defByNodeId.get(isFrom ? p.toNodeId : p.fromNodeId);
      if (!otherDef) continue;
      const endPolarity: AiSystemPolarity = p.seriesLink
        ? isFrom
          ? "negative"
          : "positive"
        : p.polarity;
      if (ambiguousEnginePort(def, terminals, endPolarity, port)) {
        return { ok: false, error: { kind: "ambiguous-engine-port", ref: isFrom ? p.fromRef : p.toRef } };
      }
      const role: TerminalRole | null = p.seriesLink
        ? null
        : terminalRole(port, isFrom, def, otherDef);
      const pick = pickTerminal(def, terminals, endPolarity, role, stacked);
      if (!pick.ok) {
        return {
          ok: false,
          error: {
            kind: "no-terminal",
            ref: isFrom ? p.fromRef : p.toRef,
            polarity: endPolarity,
            reason: pick.reason,
            available: pick.available,
          },
        };
      }
      stacked.set(pick.terminal.id, (stacked.get(pick.terminal.id) ?? 0) + 1);
      resolved.set(`${index}:${end}`, pick.terminal.id);
    }
  }

  // No wire size and no fuse rating: the engine picks both.
  const usedCableIds = new Set(system.cables.map((c) => c.id));
  const addedCables: Cable[] = pending.map((p, i) => {
    const cable: Cable = {
      id: uniqueId(`ai-${i + 1}`, usedCableIds),
      fromNodeId: p.fromNodeId,
      fromTerminalId: resolved.get(`${i}:from`)!,
      toNodeId: p.toNodeId,
      toTerminalId: resolved.get(`${i}:to`)!,
      lengthM: p.lengthM,
    };
    if (p.marker) {
      const fuseType = parseFuseMarker(p.marker);
      if (fuseType) cable.fuseTypeOverride = fuseType;
      else cable.label = p.marker;
    }
    if (p.seriesLink) cable.seriesLink = true;
    if (p.lengthEstimated) cable.lengthEstimated = true;
    return cable;
  });

  const cableIdsByConnection: string[][] = draft.connections.map(() => []);
  pending.forEach((p, i) => {
    cableIdsByConnection[p.connectionIndex].push(addedCables[i].id);
  });

  if (survivingNodes.length === 0) {
    // The assistant uses edits even for a brand-new drawing. There is no user
    // placement to preserve here: give the whole batch the same source,
    // distribution, load and return bands as full-system generation.
    // Only geometry changes; adding physical zones would also change lengths.
    layoutGeneratedComponents(addedNodes, defByNodeId, addedCables, { reserveZoneSpace: false });
  } else {
    placeAddedNodes(
      addedNodes,
      nodes,
      defByNodeId,
      [...survivingCables, ...addedCables],
    );
  }

  const candidate: System = {
    ...system,
    nodes,
    cables: [...survivingCables, ...addedCables],
  };

  const result = migrate(candidate);
  if (!result.ok) {
    return {
      ok: false,
      error: {
        kind: "invalid-system",
        message:
          result.error.kind === "not-system-shape"
            ? result.error.message
            : `File version ${result.error.fileVersion} is newer than ${result.error.supportedVersion}.`,
      },
    };
  }

  return {
    ok: true,
    system: result.system,
    addedNodeIds: addedNodes.map((n) => n.id),
    addedCableIds: addedCables.map((c) => c.id),
    removedNodeIds: [...removedNodeIds],
    removedCableIds: [...removedCableIds],
    cableIdsByConnection,
  };
}

/** Rejection text for the re-ask. The endpoint and any harness replaying the
 *  retry loop must send the same words, or the harness grades a product that
 *  doesn't ship. */
export function systemEditRejection(error: SystemEditError): string {
  switch (error.kind) {
    case "ambiguous-engine-port":
      return ambiguousEnginePortRejection(error.ref);
    case "unknown-catalog-id":
      return `Rejected: ${error.catalogIds
        .map((id) => `"${id}"`)
        .join(", ")} ${error.catalogIds.length === 1 ? "is not a catalog id" : "are not catalog ids"}. Every catalogId must be copied exactly from the catalog list in the system prompt.`;
    case "unknown-ref":
      return `Rejected: ${error.refs
        .map((r) => `"${r}"`)
        .join(", ")} ${error.refs.length === 1 ? "is not a part" : "are not parts"} of this system. Use the refs exactly as they appear in the listing, or introduce the part in components first.`;
    case "duplicate-ref":
      return `Rejected: ${error.refs
        .map((r) => `"${r}"`)
        .join(", ")} already ${error.refs.length === 1 ? "exists" : "exist"} in this system. A part in the listing stays as it is — do not re-add it. Give a genuinely new part a ref of its own.`;
    case "no-terminal":
      return error.reason === "exhausted"
        ? `Rejected: "${error.ref}" has only ${error.available} ${error.polarity} output${error.available === 1 ? "" : "s"} and every one is already in use by a circuit that was there before this change. Add a part with room for the new branches and feed it from the existing distribution, rather than doubling up a fused slot.`
        : `Rejected: "${error.ref}" has no ${error.polarity} terminal, so it cannot take a ${error.polarity} cable. Wire it on the rail it actually has, or pick a part that has both.`;
    case "invalid-system":
      return `Rejected: the change did not apply (${error.message}).`;
  }
}

function rankOfOtherEnd(
  p: { fromNodeId: string; toNodeId: string },
  nodeId: string,
  defByNodeId: Map<string, ComponentDefinition>,
): number {
  const other = p.fromNodeId === nodeId ? p.toNodeId : p.fromNodeId;
  const def = defByNodeId.get(other);
  return def ? SOURCE_RANK[def.category] : SOURCE_RANK.load;
}

/** Which rail an existing cable runs on — see `system-digest.ts`, same rule. */
function cablePolarity(
  cable: Cable,
  nodeById: Map<string, SystemNode>,
): AiSystemPolarity {
  const ends: Array<[string, string]> = [
    [cable.fromNodeId, cable.fromTerminalId],
    [cable.toNodeId, cable.toTerminalId],
  ];
  for (const [nodeId, terminalId] of ends) {
    const node = nodeById.get(nodeId);
    if (!node || node.kind !== "component") continue;
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (!def) continue;
    const terminal = effectiveTerminals(def, node).find((t) => t.id === terminalId);
    if (!terminal) continue;
    if (terminal.polarity === "positive") return "positive";
    if (terminal.polarity === "negative" || terminal.polarity === "ground") {
      return "negative";
    }
  }
  return "positive";
}

function countStuds(def: ComponentDefinition, node: ComponentNode): number {
  return effectiveTerminals(def, node).filter((t) => isStudTerminal(def, t)).length;
}

function push<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

function slug(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

function uniqueId(base: string, used: Set<string>): string {
  let id = base;
  let n = 2;
  while (used.has(id)) id = `${base}-${n++}`;
  used.add(id);
  return id;
}
